import { Device, Registration, User, xhr as sdkXhr } from "coolremote-sdk";
import { t } from "ttag";
import { isQaBypassCredentials } from "../../../models/devicePublicRegistrationTestBypass";
import { markPublicRegistrationTokenSet } from "../publicRegistrationSession";
import { DiscoveryResult, PersonalDetailsValues, SiteDetailsValues } from "./newUserFlowState";

const STUB_DELAY_MS = 500;
const STUB_DISCOVERY_DELAY_MS = 5000;

/** Used only when the real submit isn't reachable yet — see `submitRegistration`. */
const STUB_DEVICE_ID = "stub-device-id";

const delay = <T,>(value: T, ms: number): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), ms));

export const isUsernameAvailable = (username: string): Promise<boolean> =>
  User.isUsernameAvailable(username).then((result: { available: boolean }) => result.available);

export const isCustomerNameAvailable = (name: string): Promise<boolean> =>
  Registration.isCustomerNameAvailable(name).then((result: { available: boolean }) => result.available);

export interface SubmitRegistrationResult {
  userId: string;
  customerId: string;
  siteId: string;
  deviceId: string;
  token: string;
}

const callSubmitProfessionalRegistration = async (
  serial: string,
  pin: string,
  personalDetails: PersonalDetailsValues,
  customerName: string,
  siteDetails: SiteDetailsValues,
  isTestRegistration: boolean
): Promise<SubmitRegistrationResult> => {
  const response = await Registration.submitProfessionalRegistration({
    serial,
    pin,
    username: personalDetails.email,
    email: personalDetails.email,
    firstName: personalDetails.firstName,
    lastName: personalDetails.lastName,
    password: personalDetails.password,
    phone: personalDetails.phone || undefined,
    customerName,
    temperatureScale: personalDetails.temperatureScale === "" ? undefined : personalDetails.temperatureScale,
    site: {
      name: siteDetails.name,
      description: siteDetails.description || undefined,
      country: siteDetails.country || undefined,
      city: siteDetails.city || undefined,
      address: siteDetails.address || undefined,
      state: siteDetails.state || undefined,
      postalCode: siteDetails.postalCode || undefined,
      timezone: siteDetails.timezone || undefined
    },
    ...(isTestRegistration ? { isTestRegistration: true } : {})
  });

  if (!response?.token) {
    throw new Error(t`Something went wrong. Please try again`);
  }

  // In-memory only — deliberately not persisted to localStorage and not
  // reflected in the app's global `isLoggedIn` flag, so this still-mid-wizard
  // screen doesn't trigger the app's normal post-login bootstrap. See
  // `existingUserFlowApi.ts`'s `login` for the full reasoning.
  sdkXhr.setToken(response.token);
  markPublicRegistrationTokenSet();

  return {
    userId: response.id,
    customerId: response.customerId,
    siteId: response.siteId,
    deviceId: response.deviceId,
    token: response.token
  };
};

/**
 * Registers the new user, customer, site and device association in one call.
 * For the QA bypass serial/pin, tries the real endpoint first with
 * `isTestRegistration: true` (self-cleaning on BE, wipes and recreates any
 * prior test run) — and only falls back to a fully local stub if that isn't
 * reachable yet (e.g. this identity isn't on BE's test allowlist yet), so the
 * click-through demo never breaks and upgrades to the real thing on its own
 * once it is.
 */
export const submitRegistration = async (
  serial: string,
  pin: string,
  personalDetails: PersonalDetailsValues,
  customerName: string,
  siteDetails: SiteDetailsValues
): Promise<SubmitRegistrationResult> => {
  if (!isQaBypassCredentials(serial, pin)) {
    return callSubmitProfessionalRegistration(serial, pin, personalDetails, customerName, siteDetails, false);
  }

  try {
    return await callSubmitProfessionalRegistration(serial, pin, personalDetails, customerName, siteDetails, true);
  } catch {
    await delay(undefined, STUB_DELAY_MS);
    return {
      userId: "stub-user-id",
      customerId: "stub-customer-id",
      siteId: "stub-site-id",
      deviceId: STUB_DEVICE_ID,
      token: "stub-token"
    };
  }
};

/**
 * Fetches the newly-associated device's tree once for its discovered indoor
 * units — device registration has already completed successfully by this
 * point, so this is a single check, not a wait for units to appear.
 * Falls back to a paced local simulation for the QA stub device id, since
 * that one was never actually associated with anything server-side.
 */
export const discoverDevice = async (deviceId: string, serial: string): Promise<DiscoveryResult> => {
  if (deviceId === STUB_DEVICE_ID) {
    return delay({ deviceMac: serial, indoorUnitsCount: 3 }, STUB_DISCOVERY_DELAY_MS);
  }

  try {
    const tree = await Device.fetchTree(deviceId);
    const units: { type?: number }[] = Object.values(tree?.units ?? {});
    const indoorUnitsCount = units.filter((unit) => unit.type === 1).length;
    return { deviceMac: serial, indoorUnitsCount };
  } catch {
    return { deviceMac: serial, indoorUnitsCount: 0, hadError: true };
  }
};
